import { getAge } from "./get.age";
import { ErrorModel } from "./shared.types";
import { useFlowSettingsChecks } from "./use.flow.settings";


export function useCheckEligibility<F, S>(formData: F | any, settings: S | any) {
    const { ineligible } = settings;

    // Decline Words
    const { isIneligibleOccupation, isIneligibleHealthIssue } = useFlowSettingsChecks(settings);

    // Check if there's ineligibility message in settings to make sure the form is requesting for an eligibility check.
    // This would prevent forms throwing ineligibility error that don't have one of the checks below in their data  
    const checkForSettingsOption = (fieldname: string) => {
        return !!ineligible ? ineligible.hasOwnProperty(fieldname) : false;
    }

    return {
        handleEligibillity: (): ErrorModel<F> | true => {
            // if `ineligible` property is not present in `settings` always return `true`;
            if ( !ineligible ) return true;


            // DOB
            const age = getAge(formData.dob);
            if (checkForSettingsOption('dob') && age && (age > ineligible.dob.max || age < ineligible.dob.min)) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.dob.message, field: 'dob' };
            }

            // Age
            if (checkForSettingsOption('age') && formData.age && (formData.age > ineligible.age.max || formData.age < ineligible.age.min)) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.age.message, field: 'age' };
            }

            // State
            if (checkForSettingsOption('state') && formData.state && ineligible.state?.states?.includes(formData.state)) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.state.message, field: 'state' };
            }

            // Income
            if ( checkForSettingsOption('income') && formData.income ) {
                let income = parseInt((formData.income).replace(/[^\d]/g, ''), 10);
                if (income <= ineligible.income.min) {
                    return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.income.message, field: 'income' };
                }
            }




            

            // manual Occ. has Decline words
            if ( checkForSettingsOption('occ') && isIneligibleOccupation(formData.occManual) ) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.occ.message, field: 'occManual' };
            }
            if ( checkForSettingsOption('occ') && isIneligibleOccupation(formData.jobTitle) ) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.occ.message, field: 'jobTitle' };
            }

            // OccClass (Unsupported/No)
            if (checkForSettingsOption('occ') && formData.occClass && (formData.occClass === 'No' || formData.occClass === 'Unsupported')) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.occ.message, field: 'occClass' };
            }

            // Is Unemployed
            if (checkForSettingsOption('unemployed') && formData.isUnemployed === 'yes') {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.unemployed.message, field: 'isUnemployed' };
            }




            // Citizen or Resident
            if (checkForSettingsOption('citizenOrResident') && formData.isCitizenOrResident !== 'yes') {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.citizenOrResident.message, field: 'isCitizenOrResident' };
            }

            // Is Disabled
            if (checkForSettingsOption('disabled') && formData.isDisabled === 'yes') {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.disabled.message, field: 'isDisabled' };
            }

            // License revoked
            if (checkForSettingsOption('licenseRevoked') && formData.licenseRevoked === 'yes') {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.licenseRevoked.message, field: 'licenseRevoked' };
            }

            // Had Cancer
            if (checkForSettingsOption('hadCancer') && formData.hadCancer === 'yes') {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.hadCancer.message, field: 'hadCancer' };
            }

            // Is replacing Current Coverage
            if (checkForSettingsOption('replacing') && formData.isReplacing === 'yes') {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.replacing.message, field: 'isReplacing' };
            }

            // Min Number of Employees            
            if (
                checkForSettingsOption('minEmployees') && 
                formData.eligibleEmployees && 
                ineligible.minEmployees?.min && 
                parseInt((formData.eligibleEmployees).replace(/[^\d]/g, ''), 10) < ineligible.minEmployees?.min
            ) {
                return { gtm: 'alt-product', heading: ineligible.minEmployees?.headingText, text: ineligible.minEmployees.message, field: 'minEmployees' };
            }

            

            if ( settings.dataType === 'ltdiManualForm' && checkForSettingsOption('healthIssues') ) {
                const redirectHealthIssues: Array<string> = ineligible.healthIssues?.redirect || [];
                const knockoutHealthIssues: Array<string> = ineligible.healthIssues?.knockout || [];

                // Knockout / Decline 
                if ( isIneligibleHealthIssue(formData.healthIssues) ) {
                    return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.healthIssues?.message, field: 'healthIssues' };
                }
                // (must be first in check to decline if atleast one knockout health issue was selected)
                if ( !!(formData.healthIssuesList as Array<string> | null)?.find(item => knockoutHealthIssues.includes(item)) ) {
                    return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.healthIssues?.message, field: 'healthIssuesList' };
                }

                // Ineligible Redirect
                if ( !!(formData.healthIssuesList as Array<string> | null)?.find(item => redirectHealthIssues.includes(item)) ) {
                    return { gtm: 'ineligible-redirect', heading: '', text: '', field: 'healthIssuesList' };
                }

                // If Asthma + Nicotine
                if ( !!(formData.healthIssuesList as Array<string> | null)?.includes('Asthma') && formData.nicotine === 'yes' ) {
                    return { gtm: 'ineligible-redirect', heading: '', text: '', field: 'healthIssuesList' };
                }
            }


            // Hours Worked
            if (checkForSettingsOption('hoursWorked') && formData.hoursWorked && formData.hoursWorked < ineligible.hoursWorked.min) {
                return { gtm: 'knockout', heading: ineligible.headingText, text: ineligible.hoursWorked.message, field: 'hoursWorked' };
            }


            return true;
        }
    }
};